import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';

import { DvmBuilderStepBase, TileRecomputeResult } from '../../dvm-layout-builder/dvm-builder-host';
import type { DashboardTile, TargetFormulaReferenceEntry } from '../../dvm-layout-builder/dvm-layout-builder.types';
import { SelfServiceUiService } from '../../shared/self-service-ui.service';
import { extractKpiValue } from '../kpi-step/kpi-widget.utils';

/**
 * Input-control build form (INPUT_BOX / SLIDER): a dashboard input whose
 * value feeds a per-target formula into one or more target charts. The
 * target list, per-target formulas and typed references are SHELL state
 * (shared with the Interactions modal); this step owns the input's own
 * fields (default value, slider range) and the per-target formula UI.
 */
@Component({
  selector: 'app-input-control-step',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule],
  templateUrl: './input-control-step.component.html',
  styles: [':host { display: contents; }'],
})
export class InputControlStepComponent extends DvmBuilderStepBase {
  readonly layoutTypes = ['INPUT_BOX', 'SLIDER'] as const;
  private readonly http = inject(HttpClient);
  private readonly ui = inject(SelfServiceUiService);

  inputDefaultValue: number | null = null;
  sliderMin = 0;
  sliderMax = 100;
  sliderStep = 1;

  get isSliderLayout(): boolean {
    return this.host.selectedLayoutType === 'SLIDER';
  }

  // ── DvmBuilderStep ──────────────────────────────────────────────────────

  blockers(): string[] {
    return this.host.selectedTargetCharts.length ? [] : ['At least one target chart'];
  }

  buildPayload(): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      componentId: this.host.componentId,
      dashboardId: this.host.dashboardId,
      componentType: 'INPUT',
      inputType: this.host.selectedLayoutType, // 'INPUT_BOX' | 'SLIDER'
      title: this.host.title.trim(),
      dataset: this.host.selectedDatasets[0] || '',
      defaultValue: this.inputDefaultValue,
      value: this.inputDefaultValue ?? (this.isSliderLayout ? this.sliderMin : null),
      targets: this.host.selectedTargetCharts.map((value) => ({
        componentId: value,
        label: this.host.targetLabelForId(value),
        formulas: this.host.targetFormulasForPayload(value),
      })),
      ...this.host.apiCallConfigPayload(),
    };

    if (this.isSliderLayout) {
      payload['range'] = {
        min: this.sliderMin,
        max: this.sliderMax,
        step: this.sliderStep,
      };
    }

    return payload;
  }

  loadFromTile(_tile: DashboardTile, payload: Record<string, unknown>): void {
    this.inputDefaultValue = payload['defaultValue'] == null ? null : Number(payload['defaultValue']);
    const targets = Array.isArray(payload['targets']) ? payload['targets'] as Array<Record<string, unknown>> : [];
    this.host.selectedTargetCharts = targets.map((target) => String(target['componentId'] ?? '')).filter(Boolean);
    this.restoreTargetFormulas(targets, String(payload['formula'] ?? ''));
    const range = (payload['range'] as Record<string, unknown>) || {};
    this.sliderMin = Number(range['min'] ?? 0);
    this.sliderMax = Number(range['max'] ?? 100);
    this.sliderStep = Number(range['step'] ?? 1);
  }

  reset(): void {
    this.inputDefaultValue = null;
    // sliderMin/Max/Step intentionally keep their last values (as before the split).
  }

  /** Adds the tile at once; the POST only persists the configuration. */
  generate(payload: Record<string, unknown>, url: string): boolean {
    this.host.isGenerating = false;
    const updated = this.host.addTile(this.buildTile(payload));
    this.http.post<Record<string, unknown>>(url, payload).subscribe({
      next: () => this.ui.showSuccess(updated ? 'Input control updated on the dashboard.' : 'Input control added to the dashboard.'),
      error: () => this.ui.showError('Input control added, but saving its configuration failed.'),
    });
    return true;
  }

  // ── internals ───────────────────────────────────────────────────────────

  /**
   * Per-target formulas come back in three shapes: `formulas[]` entries
   * (current — with an optional `step` for WATERFALL targets and typed
   * `references`), a legacy `stepFormulas` map, or a legacy flat `formula`.
   */
  private restoreTargetFormulas(targets: Array<Record<string, unknown>>, legacyFormula: string): void {
    const targetFormulas: Record<string, string> = {};
    const targetStepFormulas: Record<string, Record<string, string>> = {};
    const targetFormulaReferences: Record<string, TargetFormulaReferenceEntry[]> = {};
    targets.forEach((target) => {
      const id = String(target['componentId'] ?? '');
      if (!id) return;
      const entries = Array.isArray(target['formulas']) ? target['formulas'] as Array<Record<string, unknown>> : null;
      if (entries) {
        const withStep = entries.filter((entry) => !!entry['step']);
        if (withStep.length) {
          const perStep: Record<string, string> = {};
          withStep.forEach((entry) => { perStep[String(entry['step'])] = String(entry['formula'] ?? ''); });
          targetStepFormulas[id] = perStep;
        } else {
          targetFormulas[id] = String(entries[0]?.['formula'] ?? '');
        }
        const referenceRows: TargetFormulaReferenceEntry[] = [];
        entries.forEach((entry) => {
          const references = entry['references'] as Record<string, Record<string, unknown>> | undefined;
          if (!references || typeof references !== 'object') return;
          const entryStep = entry['step'] ? String(entry['step']) : undefined;
          Object.keys(references).forEach((token) => {
            const ref = references[token] || {};
            referenceRows.push({
              token,
              inputTitle: String(ref['inputTitle'] ?? ''),
              targetLabel: String(ref['targetLabel'] ?? ''),
              rowLabel: String(ref['rowLabel'] ?? ''),
              step: entryStep,
            });
          });
        });
        if (referenceRows.length) targetFormulaReferences[id] = referenceRows;
      } else {
        const legacyStepFormulas = target['stepFormulas'] as Record<string, string> | undefined;
        if (legacyStepFormulas && typeof legacyStepFormulas === 'object') {
          targetStepFormulas[id] = { ...legacyStepFormulas };
        } else {
          targetFormulas[id] = String(target['formula'] ?? legacyFormula);
        }
      }
    });
    this.host.targetFormulas = targetFormulas;
    this.host.targetStepFormulas = targetStepFormulas;
    this.host.targetFormulaReferences = targetFormulaReferences;
  }

  private buildTile(payload: Record<string, unknown>): DashboardTile {
    const base = this.host.newTileBase(payload);
    base.format = this.host.defaultTileFormat(this.host.selectedLayoutType);
    const dataset = this.host.selectedDatasets[0] || '';
    const computationColumns = (this.host.cachedColumns(dataset) || []).map((name) => ({ name }));
    const value = this.inputDefaultValue ?? (this.isSliderLayout ? this.sliderMin : '');
    const numberFormat = (base.format?.['numberFormat'] as string) || 'NONE';
    const decimals = Number(base.format?.['decimals'] ?? 1);
    const title = this.host.title.trim();

    const widgetData: Record<string, unknown> = this.isSliderLayout
      ? {
          key: this.host.componentId,
          textCase: 'capital',
          numberFormat,
          decimals,
          data: {
            data: {
              name: title || 'Slider',
              min: this.sliderMin,
              max: this.sliderMax,
              step: this.sliderStep,
              rangeStart: this.inputDefaultValue ?? this.sliderMin,
              rangeEnd: this.inputDefaultValue ?? this.sliderMin,
            },
          },
          computationEntityName: dataset,
          computationColumns,
          hideCompute: true,
        }
      : {
          title: title || 'Input',
          label: title || 'Input',
          placeholder: 'Enter a value',
          entityName: dataset,
          computationEntityName: dataset,
          computationColumns,
          hideCompute: true,
          value,
          numberFormat,
          decimals,
        };

    return {
      ...base,
      colSpan: 4,
      rowSpan: 4,
      inputControl: {
        kind: this.isSliderLayout ? 'SLIDER' : 'INPUT_BOX',
        formula: this.host.selectedTargetCharts
          .map((id) => this.host.targetFormulaSummary(id))
          .filter(Boolean)
          .join(' · '),
        value,
        targets: [...this.host.selectedTargetCharts],
        widgetData,
        apiConfig: { ...this.host.buildApiCallConfig },
      },
    };
  }

  // ── Tile-side hooks (DvmBuilderStep) ──────────────────────────────────────

  applyFormatToTile(tile: DashboardTile, format: Record<string, unknown>): void {
    if (!tile.inputControl) return;
    const numberFormat = (format['numberFormat'] as string) || 'NONE';
    const decimals = Number(format['decimals'] ?? 1);
    tile.inputControl = {
      ...tile.inputControl,
      widgetData: { ...tile.inputControl.widgetData, numberFormat, decimals },
    };
  }

  /** A scalar result feeds an input control — INPUT- or KPI-labelled alike, since the backend
   *  often tags an input target as KPI: its single value becomes the control's value (slider: both range ends). */
  applyRecomputeResult(tile: DashboardTile, { result }: TileRecomputeResult): boolean {
    const value = extractKpiValue(result);
    if (value === null || typeof value === 'object' || !tile.inputControl) return false;
    tile.payload = { ...tile.payload, value };
    const isSlider = tile.layoutType === 'SLIDER';
    const widgetData = tile.inputControl.widgetData || {};
    tile.inputControl = {
      ...tile.inputControl,
      value,
      widgetData: isSlider
        ? { ...widgetData, data: { ...(widgetData['data'] as Record<string, unknown> | undefined), data: { ...((widgetData['data'] as Record<string, unknown> | undefined)?.['data'] as Record<string, unknown> | undefined), rangeStart: value, rangeEnd: value } } }
        : { ...widgetData, value },
    };
    return true;
  }
}
